import { Button } from "components/DesignSystem/Button/Button";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { EditPencil } from "components/icons/EditPencil";
import { OfficeIcon } from "components/icons/Office";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { pluralize } from "utils/pluralize";
import { Link } from "react-router-dom";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";
import {
  StateRegistrationsRes,
  StateRegistrationStatusFilter,
} from "store/apis/StateRegistrations";
import { EntityDataAttribute } from "types/Models/entity";
import { isFqDateAttr, isSosNumberAttr } from "./constants";
import { CollapsibleCard } from "./CollapsibleCard";

type EntityDetailsCardProps = {
  stateDetail: StateRegistrationsRes;
  entityAttributes: EntityDataAttribute[];
  entityName: string;
  entityPageUrl: string;
  onAddMissingInfo: () => void;
};

export const EntityDetailsCard = ({
  stateDetail,
  entityAttributes,
  entityName,
  entityPageUrl,
  onAddMissingInfo,
}: EntityDetailsCardProps) => {
  const isV2Platform = useIsGroupV2Platform();

  const editUrl = isV2Platform ? `${entityPageUrl}/profile` : entityPageUrl;

  const incorporationAttr = entityAttributes.find(
    (a) => a.name === "date_of_incorporation"
  );
  const formationTypeAttr = entityAttributes.find(
    (a) => a.name === "entity_formation_type"
  );
  const stateIdAttr = stateDetail.SECRETARY_OF_STATE.data_attributes.find((a) =>
    isSosNumberAttr(a)
  );
  const fqDateAttr = !stateDetail.is_home_state
    ? stateDetail.SECRETARY_OF_STATE.data_attributes.find((a) =>
        isFqDateAttr(a)
      )
    : null;

  const registrationDateValue = stateDetail.is_home_state
    ? (incorporationAttr?.value as string | null)
    : (fqDateAttr?.value as string | null) ?? null;

  const registrationDateLabel = stateDetail.is_home_state
    ? "Date of incorporation"
    : "Date of registration";

  const cells: { label: string; value: string | null }[] = [
    entityName ? { label: "Entity name", value: entityName } : null,
    { label: "State", value: stateDetail.state },
    formationTypeAttr?.value
      ? { label: "Entity type", value: formationTypeAttr.value as string }
      : null,
    {
      label: registrationDateLabel,
      value: registrationDateValue
        ? dayjs(registrationDateValue).format(DISPLAY_DATE)
        : null,
    },
    {
      label: "SOS Number",
      value: (stateIdAttr?.value as string | null) ?? null,
    },
  ].filter(Boolean) as { label: string; value: string | null }[];

  const missingFieldLabels = stateDetail.compliance_status.missing_field_labels;
  const hasMissingInfo = stateDetail.compliance_status.statuses.includes(
    StateRegistrationStatusFilter.MISSING_INFO
  );

  const headerTag = (
    <div className="t-flex t-items-center t-gap-2">
      {hasMissingInfo && (
        <Tag tagType="orange" size="small" icon={false} rounded>
          {pluralize(missingFieldLabels.length, "Missing info", "Missing info")}
        </Tag>
      )}
      <Link to={editUrl} onClick={(e) => e.stopPropagation()}>
        <Button size="xsmall" customType="ghost_icon">
          <EditPencil />
        </Button>
      </Link>
    </div>
  );

  return (
    <CollapsibleCard
      title="Entity details"
      icon={<OfficeIcon />}
      tag={headerTag}
      defaultOpen={false}
    >
      <div className="t-grid t-grid-cols-3 t-gap-6 t-p-5 sm:t-grid-cols-1 sm:t-gap-4 sm:t-p-4">
        {cells.map((cell) => (
          <div key={cell.label} className="t-flex t-flex-col t-gap-1">
            <span className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-text-30">
              {cell.label}
              {cell.value === null && (
                <span className="t-w-1.5 t-h-1.5 t-rounded-full t-bg-orange-70" />
              )}
            </span>
            {cell.value === null ? (
              <Button
                size="xsmall"
                customType="link"
                className="!t-text-body-sm t-self-start"
                onClick={onAddMissingInfo}
              >
                + Add
              </Button>
            ) : (
              <span className="t-text-body-sm t-font-medium t-text-text-100">
                {cell.value}
              </span>
            )}
          </div>
        ))}
      </div>
    </CollapsibleCard>
  );
};
